import { useState } from "react";
import { useTranslation } from "react-i18next";
import { openConnectorPage, startAgentSignIn, type ConnectorPageOutcome, type ConnectorPageSection } from "../../../api/connectorsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { isProblemWithCode, toUiError } from "../../../errors/uiError";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import CommandFold from "../CommandFold";
import ConnectorCommandLine, { type ConnectorCommand } from "../ConnectorCommandLine";
import { useFollowPc } from "./followPc";

/** The connector's answer, or that it went offline before it could give one. */
type Answer = ConnectorPageOutcome | "started" | "already_open" | "offline";

/** The answers after which the PC soon reports something new: a sign-in or its page is open there (F161). */
const FOLLOWED: ReadonlySet<Answer> = new Set<Answer>(["started", "already_open", "opened"]);

/** What each answer tells the person; the ones that opened nothing show the command too. */
const ANSWER_TEXT: Readonly<Record<Answer, { key: string; withCommand: boolean }>> = {
  started: { key: "signInStarted", withCommand: false },
  already_open: { key: "signInOpen", withCommand: false },
  opened: { key: "opened", withCommand: false },
  too_soon: { key: "tooSoon", withCommand: true },
  browser_never: { key: "noBrowser", withCommand: true },
  no_browser: { key: "noBrowser", withCommand: true },
  no_page: { key: "noPage", withCommand: true },
  no_answer: { key: "noAnswer", withCommand: true },
  update_needed: { key: "updateNeeded", withCommand: true },
  offline: { key: "offline", withCommand: false },
};

interface OpenOnPcProps {
  readonly connectorId: string;
  /** The PC's name, as its connector reports it. */
  readonly pc: string;
  readonly section: ConnectorPageSection;
  /** An agent to sign in there directly (F122); the page at `section` is the way where that can't start. */
  readonly signInAgent?: string | undefined;
  /** The button's words, naming the PC. */
  readonly label: string;
  /** What to type on that PC when no page opens there. */
  readonly command: ConnectorCommand;
  readonly variant?: "primary" | "secondary";
}

/**
 * An "on ‹PC›" action (F85): the server asks that PC's connector to open its own page there,
 * signed in, or to start one agent's own sign-in there (F122), and the connector's answer shows
 * here live. The browser never guesses which PC it runs on, and no app link is involved. Once a
 * sign-in or the page is open there, the page that lists the PCs follows them (F161).
 */
export default function OpenOnPc({ connectorId, pc, section, signInAgent, label, command, variant = "primary" }: OpenOnPcProps) {
  const { t } = useTranslation();
  const { state, submit } = useSubmission();
  const [answer, setAnswer] = useState<Answer | null>(null);
  const { follow: followPc } = useFollowPc();
  const open = () => {
    setAnswer(null);
    submit(async () => {
      try {
        const answered = await askPc(connectorId, section, signInAgent);
        setAnswer(answered);
        if (FOLLOWED.has(answered)) followPc();
      } catch (caught) {
        if (!isProblemWithCode(toUiError(caught), "connector_offline")) throw caught;
        setAnswer("offline");
      }
    });
  };
  const text = answer === null ? null : ANSWER_TEXT[answer];
  return (
    <div className="space-y-1">
      <AppButton type="button" variant={variant} size="sm" disabled={state.status === "submitting"} onClick={open}>
        {label}
      </AppButton>
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      <div role="status" aria-live="polite" className="space-y-1">
        {state.status === "submitting" ? <p className="app-text-muted text-xs">{t("connectors.pcPage.asking", { pc })}</p> : null}
        {text === null ? null : <p className="app-text text-xs">{t(`connectors.pcPage.answers.${text.key}`, { pc })}</p>}
        {text?.withCommand ? <CommandFold><ConnectorCommandLine command={command} /></CommandFold> : null}
      </div>
    </div>
  );
}

/** The agent's sign-in where its connector can start it; its page otherwise: an older connector, or an agent not on there. */
async function askPc(connectorId: string, section: ConnectorPageSection, signInAgent: string | undefined): Promise<Answer> {
  if (signInAgent !== undefined) {
    const signIn = await startAgentSignIn(connectorId, signInAgent);
    if (signIn !== "not_available" && signIn !== "update_needed") return signIn;
  }
  return await openConnectorPage(connectorId, section);
}
